import { Box, Flex, Image, Link } from '@chakra-ui/react';
import johnHero from '../images/johnHero.jpg';
import { Briefcase, MapPin, Github, Instagram, Globe, Linkedin } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState } from 'react';

const Hero = () => {

	// First emoji to display
	const [ currentEmoji, setCurrentEmoji ] = useState("🧙‍♂️");
	const [ isAnimating, setIsAnimating ] = useState(false);
	const animationDuration = 1000;

	const emojiList = [
		"👋",
		"🐉",
		"🧙‍♂️",
		"🔥",
		"🏰",
		"🛡️",
		"🗡️",
		"🏹",
		"🎲",
		"🎮",
		"📚",
		"📜",
		"🖋️",
		"🎨",
		"🎭",
		"🎸",
		"🎤",
		"🎬",
		"🎥",
	];

	/**
	 * Randomize an emoji from the list and return it
	 * @param currentEmoji The current emoji displayed
	 * @returns A random emoji that is not the current one
	 */
	function getRandomEmoji(currentEmoji: string) {
		let randomEmoji;
		do {
			randomEmoji = emojiList[Math.floor(Math.random() * emojiList.length)];
		} while (randomEmoji === currentEmoji);
		return randomEmoji;
	}

	const MotionFlex = motion(Flex);

	/**
	 * Handle the mouse enter event for the emoji
	 */
	function handleMouseEnter() {
		if (!isAnimating) {
			setCurrentEmoji(getRandomEmoji(currentEmoji));
			setIsAnimating(true);
			
			// Complete the animation cycle even after mouse leaves
			setTimeout(() => {
				setIsAnimating(false);
			}, animationDuration);
		}
	};

	return (
		<Flex
			id="home"
			flexDirection={{ base: "column", md: "row" }}
			alignItems="center"
			justifyContent="space-between"
			padding="5%"
			paddingTop={"80px"}
			paddingLeft={"10%"}
			paddingRight={"10%"}
			backgroundColor={"black.200"}
		>
			{/* Text Content */}
			<Flex
				flexDirection={"column"}
				width={{ base: "100%", md: "50%" }}
				gap={4}
			>
				{/* Title */}
				<Box
					fontSize={"6xl"}
					fontFamily={"Source Code Pro Variable"}
					fontWeight="600"
					textAlign="left"
					color={"white.200"}
					// when mouse enters, set a random emoji and hovered to true
					onMouseEnter={() => {
						handleMouseEnter();
					}}
				>
					<Flex
						flexDirection={"row"}
						alignItems="center"
						gap={4}
						flexWrap={"wrap"}
					>
						<Box width={"min-width"}>Hi, I’m John!</Box>
						<MotionFlex>
							<motion.div
								animate={{ rotate: isAnimating ? [0, 30, -30, 30, -30, 0] : 0 }}
							>
								<Box fontSize="5xl" zIndex={1}>
									{currentEmoji}
								</Box>
							</motion.div>
						</MotionFlex>
					</Flex>
				</Box>

				{/* Subtitle */}
				<Box
					fontSize={"md"}
					fontFamily={"Source Code Pro Variable"}
					fontWeight="500"
					textAlign="left"
					color={"white.300"}
				>
					As a UI/UX Designer and Frontend Developer, I specialize in crafting stunning digital experiences that are both user-friendly and built with cutting-edge technologies. With over four years of working experience through my own company, Zenith Software, I am passionate about continuously learning and self-improvement through hard work.
				</Box>

				<Flex
					flexDirection={"column"}
					marginTop={"50px"}
					gap={15}
					alignItems={{ base: "center", md: "flex-start" }}
				>
					{/* Extra Info */}
					<Flex
						flexDirection={"column"}
					>
						{/* Location */}
						<Flex
							alignItems="center"
							marginBottom={2}
							gap={2}
						>
							<MapPin size={30} color="#D1D5DB" />
							<Box
								fontSize={"md"}
								fontFamily={"Source Code Pro Variable"}
								fontWeight="500"
								textAlign="left"
								color={"white.300"}
							>
								New Orleans, LA
							</Box>

						</Flex>

						{/* Work status */}
						<Flex
							alignItems="center"
							marginBottom={2}
							gap={2}
						>
							<Briefcase size={30} color="#D1D5DB" />
							<Box
								fontSize={"md"}
								fontFamily={"Source Code Pro Variable"}
								fontWeight="500"
								textAlign="left"
								color={"white.300"}
							>
								Available to work
							</Box>

						</Flex>
						
					</Flex>

					{/* Icons */}
					<Flex
						flexDirection={"row"}
						gap={4}
						justifyContent={{ base: "center", md: "flex-start" }}
					>
						{/* GitHub */}
						<Flex
							alignItems="center"
							marginBottom={2}
							gap={2}
							_hover={{ color: "green.500" }}
							color="#D1D5DB"
						>
							
							<Link
								href="https://github.com/jjbajor"
								isExternal
							>
								<Github size={30} />
							</Link>

						</Flex>

						{/* LinkedIn */}
						<Flex
							alignItems="center"
							marginBottom={2}
							gap={2}
							_hover={{ color: "green.500" }}
							color="#D1D5DB"
						>
							
							<Link
								href="https://www.linkedin.com/in/johnbajor/"
								isExternal
							>
								<Linkedin size={30} />
							</Link>

						</Flex>

						{/* Instagram */}
						<Flex
							alignItems="center"
							marginBottom={2}
							gap={2}
							_hover={{ color: "green.500" }}
							color="#D1D5DB"
						>
							
							<Link
								href="https://www.instagram.com/jjbajor/"
								isExternal
							>
								<Instagram size={30} />
							</Link>

						</Flex>

						{/* Globe */}
						<Flex
							alignItems="center"
							marginBottom={2}
							gap={2}
							_hover={{ color: "green.500" }}
							color="#D1D5DB"
						>
							
							<Link
								href="/"
							>
								<Globe size={30} />
							</Link>

						</Flex>
					</Flex>
				</Flex>
					
			</Flex>

			{/* Image */}
			<Box
				flex="1"
				paddingTop={{ base: "10%", md: "0" }}
				marginBottom={{ base: "5", md: "0" }}
				maxWidth={{ base: "80%", md: "30%" }}
				borderColor={"black.200"}
				borderWidth={6}
				boxShadow={"30px 30px 0px #374151"}
			>
				<Image
				src={johnHero}
				alt="Hero Image"
				objectFit="cover"
				width="100%"
				height="auto"
				/>
			</Box>

		</Flex>
	);
};

export default Hero;
